// src/pages/DashboardPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import AnalysisNavigation from '../components/AnalysisNavigation';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import ApiService from '../services/api';

const DashboardPage = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);


  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        setError(null);

        const statusResponse = await ApiService.getAnalysisStatus(taskId);
        
        if (statusResponse.status === 'completed') {
          const resultsResponse = await ApiService.getAnalysisResults(taskId);
          setResults(resultsResponse);
        } else if (statusResponse.status === 'error') {
          setError(`Error in analysis: ${statusResponse.message}`);
        } else {
          navigate('/upload', { 
            state: { 
              message: 'The analysis is still running. Please wait until it is completed.' 
            } 
          });
        }
      } catch (error) {
        console.error('Error fetching results:', error);
        setError('Error retrieving analysis results');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [taskId, navigate]);

  const extractContent = (input) => {
    let current = typeof input === 'string' ? input : JSON.stringify(input);
    
    try {
      let parsed = JSON.parse(current);
      while (parsed && typeof parsed === 'object') {
        if (parsed.result) {
          current = parsed.result;
          parsed = typeof current === 'string' ? JSON.parse(current) : current;
        } else {
          break;
        }
      }
      return typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
    } catch {
      return current;
    }
  };

  const formatAnalysisResult = (analysisType, result) => {
    if (!result) return { title: getAnalysisTitle(analysisType), content: 'No data available' };

    try {
      const content = extractContent(result);

      if (analysisType === 'completeness') {
        const items = content.split('\n')
          .map(item => {
            const isPresent = item.includes('Present');
            const text = item.split('-')[0].replace('**', '').replace('**', '').trim();
            const number = text.match(/^\d+\./)?.[0] || '';
            const content = text.replace(/^\d+\./, '').trim();
            return { isPresent, number, content };
          })
          .filter(item => item.content);

        const midPoint = Math.ceil(items.length / 2);
        const leftColumnItems = items.slice(0, midPoint);
        const rightColumnItems = items.slice(midPoint);

        return {
          title: getAnalysisTitle(analysisType),
          content: (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {leftColumnItems.map((item, index) => (
                  <Box key={`left-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label={item.isPresent ? 'Present' : 'Missing'} 
                      color={item.isPresent ? 'success' : 'error'} 
                      size="small" 
                      sx={{ mr: 2 }} 
                    />
                    <Typography variant="body2">
                      {item.number} {item.content}
                    </Typography>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12} md={6}>
                {rightColumnItems.map((item, index) => (
                  <Box key={`right-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label={item.isPresent ? 'Present' : 'Missing'} 
                      color={item.isPresent ? 'success' : 'error'} 
                      size="small" 
                      sx={{ mr: 2 }} 
                    />
                    <Typography variant="body2">
                      {item.number} {item.content}
                    </Typography>
                  </Box>
                ))}
              </Grid>
            </Grid>
          )
        };
      }

      if (analysisType === 'elevator_pitch') {
        return {
          title: getAnalysisTitle(analysisType),
          content: (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {content}
            </Typography>
          )
        };
      }

      if (typeof result === 'object' && result.error) {
        return { 
          title: getAnalysisTitle(analysisType), 
          content: `Error: ${result.error}` 
        };
      }

      if (typeof result === 'object' && result.evaluation) {
        const overallKey = Object.keys(result.evaluation).find(key => 
          key.includes('overall') || 
          key.includes('team_assessment') || 
          key.includes('product_strength') || 
          key.includes('market_position') || 
          key.includes('financial_health')
        );
        
        const overallScore = overallKey ? result.evaluation[overallKey].score : 'N/A';
        const summary = overallKey ? result.evaluation[overallKey].summary : 'No summary available';
        
        return {
          title: getAnalysisTitle(analysisType),
          score: typeof overallScore === 'string' ? parseFloat(overallScore) : overallScore,
          summary: summary,
          detailLink: `/analysis/${taskId}/${analysisType}`
        };
      }

      return { 
        title: getAnalysisTitle(analysisType), 
        content: content 
      };

    } catch (error) {
      console.error(`Error formatting ${analysisType} result:`, error);
      return { 
        title: getAnalysisTitle(analysisType), 
        content: `Error displaying ${analysisType} data` 
      };
    }
  };

  const getAnalysisTitle = (analysisType) => {
    const titles = {
      'elevator_pitch': 'Elevator Pitch',
      'completeness': 'Completeness Analysis',
      'team': 'Team Analysis',
      'product': 'Product Analysis',
      'market': 'Market Analysis',
      'finance': 'Financial Analysis',
      'strategic_finance': 'Strategic Financial Analysis'
    };
    return titles[analysisType] || analysisType;
  };

  const getAnalysisIcon = (analysisType) => {
    switch (analysisType) {
      case 'elevator_pitch': return <LightbulbIcon fontSize="large" color="primary" />;
      case 'completeness': return <AssignmentIcon fontSize="large" color="primary" />;
      case 'team': return <GroupIcon fontSize="large" color="primary" />;
      case 'product': return <DescriptionIcon fontSize="large" color="primary" />;
      case 'market': return <BusinessIcon fontSize="large" color="primary" />;
      case 'finance': return <MonetizationOnIcon fontSize="large" color="primary" />;
      case 'strategic_finance': return <StackedBarChartIcon fontSize="large" color="primary" />;
      default: return <DescriptionIcon fontSize="large" color="primary" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ my: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/upload')}>
          Back to Upload Page
        </Button>
      </Box>
    );
  }

  if (!results || !results.analyses) {
    return (
      <Box sx={{ my: 4 }}>
        <Alert severity="warning">
          No analysis results available.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/upload')} sx={{ mt: 2 }}>
          Back to Upload Page
        </Button>
      </Box>
    );
  }

  const analyses = results.analyses;
  const elevatorPitch = analyses.elevator_pitch ? formatAnalysisResult('elevator_pitch', analyses.elevator_pitch) : null;
  const completeness = analyses.completeness ? formatAnalysisResult('completeness', analyses.completeness) : null;

  const detailedAnalyses = ['team', 'product', 'market', 'finance', 'strategic_finance']
    .filter(type => analyses[type])
    .map(type => ({
      type,
      ...formatAnalysisResult(type, analyses[type])
    }));

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Analysis Dashboard
      </Typography>
      
      <AnalysisNavigation currentTab="dashboard" />
      
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Summary
        </Typography>
        
        {elevatorPitch && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {elevatorPitch.title}
            </Typography>
            {elevatorPitch.content}
          </Box>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        {completeness && (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {completeness.title}
            </Typography>
            {completeness.content}
          </Box>
        )}
      </Paper>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {detailedAnalyses.map((analysis) => (
          <Grid item xs={12} sm={6} md={4} key={analysis.type}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 3, 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getAnalysisIcon(analysis.type)}
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {analysis.title}
                </Typography>
              </Box>
              
              {analysis.score && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Overall Rating:
                  </Typography>
                  <Chip 
                    label={`${analysis.score}/5`} 
                    color={analysis.score >= 4 ? 'success' : analysis.score >= 3 ? 'warning' : 'error'}
                  />
                </Box>
              )}
              
              <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                {analysis.summary}
              </Typography>
              
              <Button 
                variant="outlined" 
                onClick={() => navigate(analysis.detailLink)}
                sx={{ alignSelf: 'flex-start', mt: 'auto' }}
              >
                View Details
              </Button>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DashboardPage;
